/**
 * Achievements — tracked PER CHARACTER (the hero you're playing earns them on
 * their own profile).
 *
 * Two layers:
 *  - ACHIEVEMENTS: the 10 SHARED feats every hero can earn.
 *  - HERO_ACHIEVEMENTS: a handful of hero-SPECIFIC feats keyed by hero id. These
 *    reflect each hero's story/playstyle and are what gate that hero's skins.
 *
 * Each achievement checks a profile stat against a target, so tracking is just
 * bookkeeping on the per-character profile (kills, best depth, level, bosses,
 * runs). Use achievementsFor(heroId) to get the full list a given hero can earn.
 */
export const ACHIEVEMENTS = [
  { id: "firstblood", title: "First Blood",        desc: "Defeat your first foe.",          stat: "kills",  target: 1 },
  { id: "veteran",    title: "Veteran",            desc: "Defeat 250 foes.",                stat: "kills",  target: 250 },
  { id: "slayer",     title: "Slayer",             desc: "Defeat 1,000 foes.",              stat: "kills",  target: 1000 },
  { id: "delver",     title: "Delver",             desc: "Reach depth 5.",                  stat: "best",   target: 5 },
  { id: "deepdiver",  title: "Deep Diver",         desc: "Reach depth 10.",                 stat: "best",   target: 10 },
  { id: "abyssal",    title: "Into the Abyss",     desc: "Reach depth 15.",                 stat: "best",   target: 15 },
  { id: "champion",   title: "Champion",           desc: "Reach hero level 15.",            stat: "level",  target: 15 },
  { id: "ascendant",  title: "Ascendant",          desc: "Reach hero level 25.",            stat: "level",  target: 25 },
  { id: "giantslayer",title: "Giantslayer",        desc: "Defeat a boss.",                  stat: "bosses", target: 1 },
  { id: "warden",     title: "Warden of the Vault",desc: "Defeat 5 bosses.",                stat: "bosses", target: 5 },
];

/**
 * Hero-specific feats — three per hero, themed to their story. Earning ANY one
 * unlocks that hero's Ashen skin; earning ALL THREE unlocks Ember-Forged.
 * ids are prefixed with the hero id to stay unique in the per-profile map.
 */
export const HERO_ACHIEVEMENTS = {
  knight: [
    { id: "knight_gate",   title: "Hold the Gate",   desc: "Reach depth 8 — the breach Sir Aldric failed to hold.", stat: "best",   target: 8,  hero: "knight" },
    { id: "knight_honor",  title: "Reclaimed Honor", desc: "Defeat 3 bosses to answer for the gate.",               stat: "bosses", target: 3,  hero: "knight" },
    { id: "knight_iron",   title: "Unyielding",      desc: "Complete 15 runs without abandoning the Vault.",         stat: "runs",   target: 15, hero: "knight" },
  ],
  barbarian: [
    { id: "barb_blood",  title: "Bloodlust",       desc: "Defeat 500 foes in a fury.",              stat: "kills",  target: 500, hero: "barbarian" },
    { id: "barb_wrath",  title: "Mountain's Wrath", desc: "Reach hero level 20.",                    stat: "level",  target: 20,  hero: "barbarian" },
    { id: "barb_tear",   title: "Tear It Down",    desc: "Reach depth 12 to rip the Vault apart.",   stat: "best",   target: 12,  hero: "barbarian" },
  ],
  rogue: [
    { id: "rogue_score", title: "One Last Score",   desc: "Complete 20 runs chasing the big haul.",  stat: "runs",   target: 20,  hero: "rogue" },
    { id: "rogue_deep",  title: "Deeper and Richer", desc: "Reach depth 12 — no way out but down.",  stat: "best",   target: 12,  hero: "rogue" },
    { id: "rogue_blade", title: "Silent Blade",     desc: "Defeat 750 foes unseen.",                 stat: "kills",  target: 750, hero: "rogue" },
  ],
  ranger: [
    { id: "ranger_far",  title: "Farstrider",       desc: "Reach depth 10 reading the corridors.",   stat: "best",   target: 10,  hero: "ranger" },
    { id: "ranger_hunt", title: "The Long Hunt",    desc: "Defeat 600 foes from a distance.",        stat: "kills",  target: 600, hero: "ranger" },
    { id: "ranger_cold", title: "Never Caught",     desc: "Complete 12 runs and slip away each time.", stat: "runs", target: 12,  hero: "ranger" },
  ],
  mage: [
    { id: "mage_geo",    title: "Forbidden Geometry", desc: "Reach hero level 22 studying the Vault.", stat: "level",  target: 22, hero: "mage" },
    { id: "mage_master", title: "Master of the Vault", desc: "Defeat 4 bosses to prove your mastery.", stat: "bosses", target: 4,  hero: "mage" },
    { id: "mage_read",   title: "Deep Reading",     desc: "Reach depth 14 into the impossible text.", stat: "best",   target: 14, hero: "mage" },
  ],
};

/** The full achievement list a given hero can earn: shared + hero-specific. */
export function achievementsFor(heroId) {
  return ACHIEVEMENTS.concat(HERO_ACHIEVEMENTS[heroId] || []);
}
/** Just this hero's specific feats (used for skin gating). */
export function heroAchievements(heroId) {
  return HERO_ACHIEVEMENTS[heroId] || [];
}

export const ACHIEVEMENT_COUNT = ACHIEVEMENTS.length;
/** Total feats available to a hero (shared + their specific ones). */
export function achievementCount(heroId) { return achievementsFor(heroId).length; }

export function achieveProgress(profile, a) {
  const v = Math.floor((profile && profile[a.stat]) || 0);
  return { have: v, need: a.target, done: v >= a.target };
}

/**
 * Art for a feat card. When real artwork exists, set `art` on the achievement
 * (an image URL) and it's used directly. Until then we fall back to a themed
 * glyph based on the tracked stat, so cards already have a distinct visual slot.
 */
const FEAT_GLYPH = { kills: "†", best: "▼", level: "✦", bosses: "☠", runs: "⟳" };
export function featArt(a) {
  if (a && a.art) return `<img class="feat-art-img" src="${a.art}" alt="">`;
  const g = (a && FEAT_GLYPH[a.stat]) || "★";
  return `<span class="feat-art-glyph">${g}</span>`;
}

/** Count how many of this hero's feats the profile has earned (shared + specific). */
export function earnedCount(profile, heroId) {
  const got = (profile && profile.achievements) || {};
  return achievementsFor(heroId).reduce((n, a) => n + (got[a.id] ? 1 : 0), 0);
}
/** How many of this hero's SPECIFIC feats are earned. */
export function heroEarnedCount(profile, heroId) {
  const got = (profile && profile.achievements) || {};
  return heroAchievements(heroId).reduce((n, a) => n + (got[a.id] ? 1 : 0), 0);
}
/** True once every feat (shared + specific) is earned for this hero. */
export function allEarned(profile, heroId) {
  return earnedCount(profile, heroId) >= achievementCount(heroId);
}

// ---- Unlockable skins (recolor tints; the pack ships one texture per hero, so
// alternate "skins" are runtime colour washes). Skins are now gated by a hero's
// SPECIFIC feats, not level:
//   ashen        — earn ANY 1 of the hero's specific feats
//   emberforged  — earn ALL of the hero's specific feats
//   prestige     — master EVERYTHING (shared + specific) on that hero
// ----
export const SKINS = [
  { id: "default",     name: "Default",      tint: null,     req: "always" },
  { id: "ashen",       name: "Ashen",        tint: 0x9aa6c0, req: "anyfeat" },
  { id: "emberforged", name: "Ember-Forged", tint: 0xff8a4a, req: "allfeats" },
];
export const PRESTIGE_SKIN = { id: "prestige", name: "Vaultgold", tint: 0xf0c34b, req: "master" };
const ALL_SKINS = [...SKINS, PRESTIGE_SKIN];

/** Is a skin unlocked for this hero/profile? */
export function skinUnlocked(skin, profile, heroId) {
  const total = heroAchievements(heroId).length;
  const earned = heroEarnedCount(profile, heroId);
  switch (skin.req) {
    case "always":   return true;
    case "anyfeat":  return earned >= 1;
    case "allfeats": return total > 0 && earned >= total;
    case "master":   return allEarned(profile, heroId);
    default:         return true;
  }
}
/** Short label describing how a locked skin is earned. */
export function skinRequirement(skin, heroId) {
  const total = heroAchievements(heroId).length;
  switch (skin.req) {
    case "anyfeat":  return "1 hero feat";
    case "allfeats": return `all ${total} hero feats`;
    case "master":   return "✦ master all";
    default:         return "";
  }
}

/** Skins available to a hero given their earned feats. */
export function unlockedSkins(profile, heroId) {
  return ALL_SKINS.filter((s) => skinUnlocked(s, profile, heroId));
}
export function skinById(id) {
  return ALL_SKINS.find((s) => s.id === id) || SKINS[0];
}
